import { Menu } from 'components/Menu/Menu'
import { Section } from '@react-stately/collections'
import {
  AI,
  BrowserCode,
  Code4,
  CoverImage,
  Data,
  Divider,
  ImageAdd,
  LinkAngled,
  Mention,
  Menu2,
  Table,
} from 'blaze-ui/icons'
import { MenuProps } from 'components/Menu/Types'
import { Key } from 'react'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { observer } from 'mobx-react-lite'
import { insertTable } from 'components/DocRightRail/screens/InsertMenu/utils/insertTable'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { openInsertEmbedPromptAndReturnNode } from 'proseShared/prompts/EmbedPrompt/EmbedPrompt'
import { insertAndFocus } from 'common/Prose/utils/insertAndFocus'
import { useOpenCoverImagePrompt } from 'components/DocRightRail/screens/InsertMenu/insertButtonHooks/useOpenCoverImagePrompt'
import { useDocStore } from 'stores/DocStore'
import { useDiagramButton } from 'components/DocRightRail/screens/InsertMenu/insertButtonHooks/useDiagramButton'
import { DEFAULT_STYLE } from 'common/Prose/nodeViews/LineDivider/types'
import { useCodeBlockButton } from 'components/DocRightRail/screens/InsertMenu/insertButtonHooks/useCodeBlockButton'
import { schema } from 'prose-core'
import { useOpenLinkPrompt } from 'components/DocRightRail/screens/InsertMenu/insertButtonHooks/useOpenLinkPrompt'
import { useCanViewAs } from 'components/Can'
import { UserRole } from 'types'
import { ItemElement } from '@react-types/shared'
import { Text } from 'blaze-ui/Text/Text'
import { Item } from 'components/Menu/MenuItem'
import { ChevronRightSmall } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import { ImageMenuDrawer } from 'components/ImageMenu/ImageMenuDrawer/ImageMenuDrawer'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { insertImageIntoDoc, processSelectedImages } from 'components/ImageMenu/utils'

interface Props extends Pick<MenuProps<any>, 'menuType' | 'onAction'> {
  hiddenKeys?: Set<Key>
}

export const InsertMenu = observer(({ menuType = 'dropdown', onAction, hiddenKeys = new Set() }: Props) => {
  const { drawersStore } = useRootStore()
  const docStore = useDocStore()
  const view = useEditorView()
  const denseTable = useFlipperFlag('dense_table')
  const openCoverImagePrompt = useOpenCoverImagePrompt(view, docStore.articleId)
  const addDiagram = useDiagramButton(view)
  const addCodeBlock = useCodeBlockButton(view)
  const openLinkPrompt = useOpenLinkPrompt(view)

  const handleAction = async (action: Key) => {
    onAction?.(action)
    switch (action) {
      case 'insertImage':
        return drawersStore.openDrawer(ImageMenuDrawer, {
          onUse: async (images: ImageMenuSelectedImage[]) => {
            const processedImages = await processSelectedImages(images, { showProcessingToast: true })

            processedImages.forEach((image) => {
              if (!image) return

              insertImageIntoDoc(image, view)
            })
            drawersStore.closeDrawer()
          },
          maxCount: 1,
          hiddenProviders: ['stockVideo'],
        })
      case 'insertTable':
        return insertTable(view, { rows: 2, columns: 2 }, denseTable)
      case 'insertEmbed':
        const embedNode = await openInsertEmbedPromptAndReturnNode({ view, centered: true })

        if (embedNode) {
          insertAndFocus(view, embedNode)
        }
        return
      case 'insertCoverImage':
        return openCoverImagePrompt()
      case 'insertDiagram':
        return addDiagram()
      case 'insertDivider':
        return insertAndFocus(view, view.state.schema.nodes.line_divider.createAndFill({ lineStyle: DEFAULT_STYLE }))
      case 'insertCodeBlock':
        return addCodeBlock()
      case 'insertTableOfContents':
        const node = schema.nodes.table_of_contents.createAndFill({})
        if (!node) {
          throw new Error(`Failed to create table_of_contents node`)
        }
        return insertAndFocus(view, node)
      case 'insertLink':
        return openLinkPrompt()
      case 'insertMention':
        return view.dispatch(view.state.tr.insertText('@'))
    }
  }

  const disabledKeys = new Set(['insertGeneratedImage'])

  const canViewAs = useCanViewAs(docStore._almanac_file?.workspace.id || null)
  if (!canViewAs([UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN])) {
    disabledKeys.add('insertMention')
  }

  const Chevron = menuType === 'tray' ? ChevronRightSmall : undefined

  return (
    <Menu onAction={handleAction} disabledKeys={disabledKeys} menuType={menuType} aria-label="Add Elements">
      <Section>
        {
          [
            !hiddenKeys.has('insertImage') && (
              <Item key="insertImage" textValue="Insert image" frontIcon={ImageAdd} endIcon={Chevron}>
                Add Image
              </Item>
            ),
            !hiddenKeys.has('insertGeneratedImage') && (
              <Item key="insertGeneratedImage" textValue="Insert Generated Image" frontIcon={AI} endIcon={Chevron}>
                Generated Image
                <Text variant="secondary">Coming Soon</Text>
              </Item>
            ),
            !hiddenKeys.has('insertTable') && (
              <Item key="insertTable" textValue="Insert Table" frontIcon={Table}>
                Table
              </Item>
            ),
            !hiddenKeys.has('insertEmbed') && (
              <Item key="insertEmbed" textValue="Insert Embed" frontIcon={BrowserCode} endIcon={Chevron}>
                Embed
              </Item>
            ),
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
      <Section>
        {
          [
            !hiddenKeys.has('insertCoverImage') && (
              <Item key="insertCoverImage" textValue="Insert Cover Image" frontIcon={CoverImage}>
                Cover Image
              </Item>
            ),
            !hiddenKeys.has('insertDiagram') && (
              <Item key="insertDiagram" textValue="Insert Diagram" frontIcon={Data}>
                Diagram
              </Item>
            ),
            !hiddenKeys.has('insertDivider') && (
              <Item key="insertDivider" textValue="Insert Divider" frontIcon={Divider}>
                Divider
              </Item>
            ),
            !hiddenKeys.has('insertCodeBlock') && (
              <Item key="insertCodeBlock" textValue="Insert Code Block" frontIcon={Code4}>
                Code Block
              </Item>
            ),
            !hiddenKeys.has('insertTableOfContents') && (
              <Item
                key="insertTableOfContents"
                textValue="Insert Table of Contents"
                frontIcon={Menu2}
                endIcon={Chevron}
              >
                Table of Contents
              </Item>
            ),
            !hiddenKeys.has('insertLink') && (
              <Item key="insertLink" textValue="Insert Link" frontIcon={LinkAngled} endIcon={Chevron}>
                Link
              </Item>
            ),
            !hiddenKeys.has('insertMention') && (
              <Item key="insertMention" textValue="Insert Mention" frontIcon={Mention} endIcon={Chevron}>
                Mention
              </Item>
            ),
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
    </Menu>
  )
})
